import { matchPath } from 'react-router-dom';
import { UserDTO } from '../../components/user-management/auth.type';
import { styled, Menu } from '@mui/material';
import { enUS, enGB } from 'date-fns/locale';
import { format as formatDateFns, parse, parseISO, isValid } from 'date-fns';
import { PAGES_STRING_REGEX } from '../constants/general';

export function isEmailValid(email: string): boolean {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
}

export function isPostalCodeValid(postalcode: string): boolean {
  return /^(?:\d{5}(?:-\d{4})?)?$/.test(postalcode);
}

export function validateName(name: string, isRequired = true): string {
  if (!name || !name.trim()) {
    return isRequired ? 'This field is required' : '';
  }

  const trimmedName = name.trim();

  // Allow letters, spaces, apostrophes, and hyphens only
  if (!/^[A-Za-z\s'-]+$/.test(trimmedName)) {
    return 'Name can only contain letters, spaces, apostrophes, and hyphens';
  }

  if (trimmedName.length < 2) {
    return 'Name must be at least 2 characters long';
  }

  return '';
}

export function filterDigitsHyphens(value: string): string {
  return value.replace(/[^0-9-]/g, '');
}

export function removeLeadingAndTrailingSlashes(string: string) {
  return string.replace(/^[\\/]+|[\\/]+$/g, '');
}

// find next element of el from arr
export function getNext(arr: string[], el: string) {
  const index = arr.indexOf(el);
  if (index > -1 && arr[index + 1]) {
    return arr[index + 1];
  } else {
    return undefined;
  }
}

export const formatDateToStringYMDWithDash = (date: string) => {
  // Transforms a string date with format yyyy-MM-dd'T'HH:mm:ss.SSSZZZ
  // to a string with format "Y-m-d"
  const newDate = new Date(date);
  if (isNaN(newDate.getMilliseconds())) {
    return '';
  }
  const dateParts = newDate.toLocaleDateString('en-US').split('/');
  const m = dateParts[0];
  const d = dateParts[1];
  const y = dateParts[2];

  return `${y}-${m}-${d}`;
};

export function parsePeriodDateToISO(value: string): string {
  if (!value) return '';
  // Strip time component from backend ISO datetime strings (e.g. "2025-01-31T00:00:00")
  const dateOnly = value.split('T')[0].trim();
  // Normalize "30th"/"1st"/"2nd"/"3rd" -> "30"/"1"/"2"/"3" and drop trailing periods
  // after abbreviated month names (e.g. "Sept.") so both can match the same formats below.
  const normalized = dateOnly.replace(/(\d{1,2})(st|nd|rd|th)\b/i, '$1').replace(/\.(?=\s)/g, '');

  const numericFormats: [RegExp, string[]][] = [
    [/^\d{4}-\d{1,2}-\d{1,2}$/, ['yyyy-MM-dd', 'yyyy-M-d']],
    [/^\d{4}\/\d{1,2}\/\d{1,2}$/, ['yyyy/MM/dd', 'yyyy/M/d']],
    [/^\d{1,2}\/\d{1,2}\/\d{4}$/, ['M/d/yyyy']],
    [/^\d{1,2}\/\d{1,2}\/\d{2}$/, ['M/d/yy']],
    [/^\d{1,2}-\d{1,2}-\d{4}$/, ['M-d-yyyy']],
    [/^\d{1,2}-\d{1,2}-\d{2}$/, ['M-d-yy']],
  ];
  const wordFormats: [RegExp, string[]][] = [
    [/^[A-Za-z]{3,}\s+\d{1,2},?\s*\d{4}$/, ['MMMM d, yyyy', 'MMMM d yyyy', 'MMM d, yyyy', 'MMM d yyyy']],
    [/^\d{1,2}\s+[A-Za-z]{3,},?\s*\d{4}$/, ['d MMMM yyyy', 'd MMMM, yyyy', 'd MMM yyyy', 'd MMM, yyyy']],
  ];

  for (const [regex, fmts] of [...numericFormats, ...wordFormats]) {
    if (!regex.test(normalized)) continue;
    for (const fmt of fmts) {
      const d = parse(normalized, fmt, new Date());
      if (isValid(d)) return formatDateFns(d, 'yyyy-MM-dd');
    }
  }
  return '';
}

export function formatISODate(isoString: string, formatMonthShort = false): string {
  if (!isoString) {
    return ''; // Return an empty string if the input is falsy
  }

  // Use parse() for date-only strings to get local midnight, avoiding the UTC-to-local
  // off-by-one-day shift that parseISO/new Date() cause for date-only ISO strings.
  const datePart = isoString.split('T')[0];
  const date = /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? parse(datePart, 'yyyy-MM-dd', new Date()) : parseISO(isoString);
  if (isNaN(date.getTime())) {
    // Check if the date is invalid
    return '';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: formatMonthShort ? 'short' : 'long',
    day: 'numeric',
  });
}

export function getFormattedCurrentDate() {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return formattedDate;
}

export function getFormattedCurrentTime(): string {
  const currentTime = new Date();
  return currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDate(dateStr: any) {
  if (!dateStr || dateStr == '') return '';

  let date: Date;
  if (typeof dateStr === 'string') {
    // Date-only strings are built from their parts, so they stay local midnight rather than UTC
    // midnight (which caused an off-by-one in US timezones) AND so single-digit month/day still
    // parse — parseISO requires zero-padding and returns Invalid Date for '2024-3-5'.
    const dateOnly = dateStr.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dateOnly) {
      date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    } else {
      date = parseISO(dateStr);
      if (!isValid(date)) date = new Date(dateStr);
    }
  } else {
    date = new Date(dateStr);
  }
  if (!isValid(date)) {
    return '';
  }
  let month = '' + (date.getMonth() + 1),
    day = '' + date.getDate();
  const year = date.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
}

export const placeParams = (pathRegex: string, params: any) => {
  //https://stackoverflow.com/a/50424896/1627360
  const segments = pathRegex.split('/');
  return segments
    .map(segment => {
      const offset = segment.indexOf(':') + 1;
      if (!offset) return segment;

      const key = segment.slice(offset);
      return params[key] ?? 'undefined';
    })
    .join('/');
};

export const matchPathInArray = (pathsArray: string[], currentLocation: string) => {
  const find = pathsArray.find(path => !!matchPath(path, currentLocation));
  return !!find;
};

export function deepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

export function formatCurrency(input: string, showCurrencySign = true, roundOff = false, alwaysTwoDecimals = false) {
  // Handle null, undefined, empty string, or '-' by defaulting to 0
  if (!input || input === '-' || input === 'null' || input === 'undefined') {
    input = '0';
  }

  let value = parseFloat(input);
  if (isNaN(value)) {
    // If still NaN after normalization, default to 0
    value = 0;
  }
  if (roundOff) {
    value = Math.round(value);
  }
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const isInteger = Number.isInteger(absValue);

  let formatted = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: alwaysTwoDecimals ? 2 : isInteger ? 0 : 2,
    maximumFractionDigits: alwaysTwoDecimals ? 2 : undefined,
  }).format(absValue);

  if (!showCurrencySign) {
    formatted = formatted.replace('$', '').trim();
  }
  if (isNegative) {
    formatted = `(${formatted})`;
  }

  return formatted;
}

export const formatBudget = (input?: number | string, showCurrencySign = true): string => {
  // Handle null, undefined, empty string, or '-' by defaulting to 0
  if (!input || input === '-' || input === 'null' || input === 'undefined') {
    input = 0;
  }

  const value = typeof input === 'string' ? parseFloat(input) : input;
  if (isNaN(value)) {
    // If still NaN after normalization, default to 0
    const normalizedValue = 0;
    return formatCurrency(normalizedValue.toString(), showCurrencySign);
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Use formatCurrency for values < 100K
  if (absValue < 100_000) {
    return formatCurrency(absValue.toString(), showCurrencySign);
  }

  let formattedValue: string;

  if (absValue >= 100_000 && absValue < 1_000_000) {
    formattedValue = `${(absValue / 1000).toFixed(1)}K`; // Convert to K format
  } else {
    formattedValue = `${(absValue / 1_000_000).toFixed(1)}M`; // Convert to M format
  }

  // Add currency sign if needed
  if (showCurrencySign) {
    formattedValue = `$${formattedValue}`;
  }

  // Handle negative numbers (show in parentheses)
  if (isNegative) {
    formattedValue = `(${formattedValue})`;
  }

  return formattedValue;
};

export function formatPercentage(input: string, showNegativeSign = true) {
  // Handle null, undefined, empty string, or '-' by defaulting to 0
  if (!input || input === '-' || input === 'null' || input === 'undefined') {
    input = '0';
  }

  const value = parseFloat(input);
  if (isNaN(value)) {
    // If still NaN after normalization, default to 0
    return '0%';
  }
  const absValue = Math.abs(value);
  // Always render two decimal places, e.g. 50 → "50.00%"
  const formatted = absValue?.toFixed(2);
  return showNegativeSign && value < 0 ? `(${formatted})%` : `${formatted}%`;
}

export function formatNumber(value: number, decimalPlaces: number) {
  if (isNaN(value)) return value.toString();
  return Math.floor(value) === value ? value.toString() : value.toFixed(decimalPlaces);
}

/**
 * Formats a negative numeric string using accounting bracket notation.
 * e.g. "-0.55" → "(-0.55)", "12" → "12", "" → ""
 * Preserves the original string representation (decimal places etc.).
 */
export function formatNegativeWithBrackets(value: string): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return trimmed;
  const num = parseFloat(trimmed);
  if (isNaN(num) || num >= 0) return trimmed;
  return `(${trimmed.replace(/^-/, '')})`;
}

export function formatNumberWithCommas(value: string, showNegativeSign = true) {
  const parsedValue = parseFloat(value);
  if (isNaN(parsedValue)) return value;
  const absValue = Math.abs(parsedValue);
  const formattedValue = Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(absValue);
  return showNegativeSign && parsedValue < 0 ? `(${formattedValue})` : formattedValue;
}

export function format(number: string | number | null | undefined, formatType: string, showNegativeSign = true) {
  // Handle null, undefined, or convert number to string if needed
  let value: string;
  if (number === null || number === undefined) {
    value = '0';
  } else if (typeof number === 'number') {
    value = number.toString();
  } else if (number === '-' || number === 'null' || number === 'undefined') {
    value = '0';
  } else {
    value = number;
  }

  switch (formatType) {
    case 'currency':
      return formatCurrency(value, showNegativeSign);
    case 'percentage':
      return formatPercentage(value, showNegativeSign);
    case 'number':
      return formatNumberWithCommas(value, showNegativeSign);
    default:
      return value;
  }
}

export const extendAllElements = (array: any[], fields: any) => {
  return array.map(item => {
    return { ...item, ...fields };
  });
};

export const parseNumber = (value: string) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? 0 : parsedValue;
};

export const compareAlphabetically = (a: string, b: string) => {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  if (aLower < bLower) {
    return -1;
  }
  if (aLower > bLower) {
    return 1;
  }
  return 0;
};

export const compareNumerically = (a: string, b: string) => {
  // Parse the string to a number using the unary plus (+)
  const numA = +a;
  const numB = +b;

  // Compare the two numbers
  return numA - numB;
};

export const sortDivisions = (a: any, b: any) => {
  // Check if 'N/A'
  if (a.divisionCode === 'N/A') return 1;
  if (b.divisionCode === 'N/A') return -1;

  // Otherwise, sort numerically based on codes
  return parseFloat(a.divisionCode) - parseFloat(b.divisionCode);
};

export function flattenArray<T>(arrays: T[][], startIndex: number, endIndex: number): T[] {
  startIndex = Math.max(startIndex, 0);
  endIndex = Math.min(endIndex, arrays.length - 1);

  const slicedArrays = arrays.slice(startIndex, endIndex + 1);
  const flattenedArray = slicedArrays.reduce((acc: T[], currArray: T[]) => acc.concat(currArray), []);

  return flattenedArray;
}

export const getFullName = (user: UserDTO) => {
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  return `${firstName}${lastName ? ` ${lastName}` : ''}`.trim();
};

export const joinDateAndUser = (date?: string, user?: UserDTO, prefix?: string) => {
  let result = prefix ? prefix : '';
  if (date) {
    const formattedDate = formatISODate(date, true);
    if (formattedDate) {
      result += formattedDate;
    }
  }
  if (user) {
    result += ' by ' + getFullName(user);
  }
  return result;
};
export const joinDateAndUser2 = (date?: string, user?: string, prefix?: string) => {
  let result = prefix ? prefix : '';
  if (date) {
    const formattedDate = formatISODate(date, true);
    if (formattedDate) {
      result += formattedDate;
    }
  }
  if (user) {
    result += ' by ' + user;
  }
  return result;
};
export const joinDateTimeAndUser = (date?: string, user?: UserDTO, prefix?: string) => {
  let result = prefix ? prefix : '';
  if (date) {
    const formattedDate = formatDateTimeDescriptive(date);
    if (formattedDate) {
      result += formattedDate;
    }
  }
  if (user) {
    result += ' by ' + getFullName(user);
  }
  return result;
};

export const min = <T>(array: T[], selector: (item: T) => number): number => {
  if (array.length === 0) {
    return 0;
  }
  return array.reduce((minValue, cur) => {
    const curValue = selector(cur);
    return curValue < minValue ? curValue : minValue;
  }, selector(array[0]));
};

export const max = <T>(array: T[], selector: (item: T) => number): number => {
  if (array.length === 0) {
    return 0;
  }

  return array.reduce((maxValue, cur) => {
    const curValue = selector(cur);
    return curValue > maxValue ? curValue : maxValue;
  }, selector(array[0]));
};

export const average = <T>(array: T[], selector: (item: T) => number): number =>
  array.reduce((acc, cur) => acc + selector(cur), 0) / (array.length === 0 ? 1 : array.length);

export const minDate = <T>(array: T[], selector: (item: T) => Date): Date | undefined => {
  if (array.length === 0) {
    return undefined;
  }

  return array.reduce((minValue, cur) => {
    const curValue = selector(cur);
    return curValue < minValue ? curValue : minValue;
  }, selector(array[0]));
};

export const maxDate = <T>(array: T[], selector: (item: T) => Date): Date | undefined => {
  if (array.length === 0) {
    return undefined;
  }

  return array.reduce((maxValue, cur) => {
    const curValue = selector(cur);
    return curValue > maxValue ? curValue : maxValue;
  }, selector(array[0]));
};

export const getYearsAndMonthsInBetween = (startDate: Date, endDate: Date) => {
  const years = endDate.getFullYear() - startDate.getFullYear();
  const months = endDate.getMonth() - startDate.getMonth();
  return { years, months };
};

export function camelCaseToSpaceSeparated(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between consecutive capitals when the second capital is followed by a lowercase letter
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const StyledMenu = styled(Menu)(({ theme }) => ({
  transform: 'translateY(-20px) translateX(10px)',
  '& .MuiMenuItem-root': {
    fontSize: '0.9rem',
    padding: '9px 15px',
    backgroundColor: '#0283a0',
    marginBottom: '10px',
    borderRadius: '4px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.4)',
    color: '#ffffff',
    fontWeight: '500',
    '&:last-child': {
      marginBottom: '0',
    },
    '&:hover': {
      opacity: 0.9,
      backgroundColor: '#0283a0',
      color: '#ffffff',
    },
    '&.Mui-disabled': {
      opacity: 0.65,
    },
  },
}));
export const capitalize = (input?: string) => {
  if (!input) return null;
  return input
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const extractDate = (dateTime: string | null | undefined): string | null => {
  if (!dateTime) return null;
  return dateTime.split('T')[0]; // Extract only the YYYY-MM-DD part
};

export const compareCaseInsensitive = (string1: string, string2: string) => {
  return string1.toLowerCase() === string2.toLowerCase();
};

// Utility function to calculate map center and zoom level
export const calculateMapCenterAndZoom = (latitudes: number[], longitudes: number[], mapWidth: number, mapHeight: number) => {
  if (latitudes.length === 0 || longitudes.length === 0) return null;

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  const avgLat = (minLat + maxLat) / 2;
  const avgLng = (minLng + maxLng) / 2;

  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;

  // Constants for zoom calculation
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 13;

  const latRad = (lat: number): number => {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const calculateZoomLevel = (mapPx: number, worldPx: number, fraction: number): number => {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  };

  const latFraction = (latRad(maxLat) - latRad(minLat)) / Math.PI;
  const lngFraction = lngDiff / 360;

  const latZoom = calculateZoomLevel(mapHeight, WORLD_DIM.height, latFraction);
  const lngZoom = calculateZoomLevel(mapWidth, WORLD_DIM.width, lngFraction);

  const zoomLevel = Math.min(latZoom, lngZoom, ZOOM_MAX);

  return { center: { lat: avgLat, lng: avgLng }, zoomLevel };
};

export const addOpacityToHexColor = (hexColor: string, opacityPercentage: number) => {
  const opacity = Math.min(Math.max(opacityPercentage, 0), 100) / 100;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const parseCurrency = (value: string | number): number => {
  if (typeof value === 'number') return value;

  if (typeof value !== 'string') return 0;

  const trimmed = value.trim();

  // Detect if the value is in negative accounting format (e.g. ($1,234.56) or $(1,234.56))
  const isNegative = /^\(\s*\$?.*?\s*\)$/.test(trimmed) || /^\$\s*\(.*?\)$/.test(trimmed);

  // Clean out common currency formatting artifacts (including OCR-introduced ":" in numeric cells)
  const cleaned = trimmed.replace(/[$,()\s:]/g, '');

  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return 0;

  return isNegative ? -parsed : parsed;
};

export const areSame = (a: any, b: any) => {
  const val1 = JSON.stringify(a);
  const val2 = JSON.stringify(b);
  return val1 === val2;
};

export const formatDateTime = (utcDate: string): string => {
  if (!utcDate) return '';
  if (!utcDate.endsWith('Z')) utcDate = `${utcDate}Z`;

  const dateObj = new Date(utcDate); // Convert UTC string to Date object

  return new Intl.DateTimeFormat(navigator.language, {
    year: '2-digit', // Uses YY format
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // Automatically adjusts based on locale
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Get user's time zone
  }).format(dateObj);
};

export const formatDateTimeDescriptive = (utcDate: string): string => {
  if (!utcDate) return '';
  if (!utcDate.endsWith('Z')) utcDate = `${utcDate}Z`;

  const dateObj = new Date(utcDate); // Convert UTC string to Date object

  return new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // Automatically adjusts based on locale
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Get user's time zone
  }).format(dateObj);
};

export const getUserLocale = (): Locale => {
  const locale = navigator.language;
  if (locale.startsWith('en-GB') || locale.startsWith('fr')) return enGB;
  return enUS;
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const formattedValue = (bytes / Math.pow(1024, i)).toFixed(decimals);

  return `${formattedValue} ${sizes[i]}`;
};

export const isHeicFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  return file.type === 'image/heic' || file.type === 'image/heif' || lowerName.endsWith('.heic') || lowerName.endsWith('.heif');
};

export function unescapeCSS(str: string) {
  return str.replace(/\\([0-9a-fA-F]{1,6} ?|[^0-9a-fA-F])/g, (_, escape) => {
    if (escape.length > 1 && /^[0-9a-fA-F]/.test(escape)) {
      // Unicode escape
      const hex = escape.trim();
      return String.fromCodePoint(parseInt(hex, 16));
    } else {
      // Escaped char (like \!, \ )
      return escape;
    }
  });
}

export function checkIsPageRangeValid(input: string): boolean {
  const trimmedInput = input.trim();

  if (trimmedInput === '') return true;
  if (!PAGES_STRING_REGEX.test(trimmedInput)) return false;

  const parts = trimmedInput.split(',');

  for (const part of parts) {
    if (part.includes('-')) {
      const [startStr, endStr] = part.split('-');
      const start = Number(startStr);
      const end = Number(endStr);

      if (start >= end) return false;
    }
  }

  return true;
}

export interface OCRFixResult {
  originalValue: string;
  fixedValue: string;
  needsFix: boolean;
  confidence: number;
  fieldName: string;
  /** If false, issue is flag-only (highlighted but not changed by Fix currency). Default true. */
  fixable?: boolean;
  /** True when a fixable issue exists together with non-fixable content in the same cell. */
  hasNonFixableContent?: boolean;
}

/**
 * Analyzes a currency value for potential OCR comma/period confusion
 * Applies the rule: comma should have at least 3 digits following it, period should have 2 digits following it
 */
export const analyzeOCRCurrencyValue = (value: string, fieldName: string): OCRFixResult => {
  if (typeof value !== 'string' || !value.trim()) {
    return {
      originalValue: value,
      fixedValue: value,
      needsFix: false,
      confidence: 1,
      fieldName,
    };
  }

  const trimmed = value.trim();

  // If letters make up at least as many characters as digits, the value is descriptive text
  // rather than a currency value — skip OCR analysis entirely.
  const nonWhitespace = trimmed.replace(/\s/g, '');
  const letterCount = (nonWhitespace.match(/[a-zA-Z]/g) || []).length;
  const digitCount = (nonWhitespace.match(/\d/g) || []).length;
  if (letterCount > 0 && letterCount > digitCount) {
    return { originalValue: value, fixedValue: value, needsFix: false, confidence: 1, fieldName };
  }

  // --- Mixed/non-fixable signals ---
  // Keep these checks, but do not early-return; we still want to fix commas/periods in
  // mixed cells like "S 400.520.00", then keep the remaining non-fixable part highlighted.
  // 1. Any letter in a value that looks numeric (contains at least one digit)
  const hasDigitAndLetter = /\d/.test(trimmed) && /[a-zA-Z]/.test(trimmed);

  // 2. $ appears inside the value (not as the first character)
  const dollarNotAtStart = trimmed.indexOf('$') > 0;

  // --- Fixable issues (comma/period/space) ---

  // Check using the same rules as our fixing logic:
  // - Comma should have 3+ digits following it
  // - Period should have 2 digits following it
  // - Space + exactly two digits at end (e.g. "9870 00") should be decimal ("9870.00")
  const hasInvalidComma = /,(\d{1,2})(?!\d)/.test(trimmed);
  const hasInvalidPeriod = /\.(\d{3,})/.test(trimmed);
  const hasSpaceBeforeTwoDigitsAtEnd = /\s(\d{2})$/.test(trimmed);

  if (!hasInvalidComma && !hasInvalidPeriod && !hasSpaceBeforeTwoDigitsAtEnd) {
    // No fixable punctuation issue. If there is non-fixable mixed content, keep as flag-only.
    if (hasDigitAndLetter || dollarNotAtStart) {
      return {
        originalValue: value,
        fixedValue: value,
        needsFix: true,
        confidence: 0.7,
        fieldName,
        fixable: false,
      };
    }
    return {
      originalValue: value,
      fixedValue: value,
      needsFix: false,
      confidence: 1,
      fieldName,
    };
  }

  let fixedValue = trimmed;
  let needsFix = false;
  let confidence = 1;

  // Space + exactly two digits at end (e.g. "9870 00") -> decimal ("9870.00")
  const spaceBeforeTwoDigitsAtEnd = /\s(\d{2})$/g;
  if (spaceBeforeTwoDigitsAtEnd.test(trimmed)) {
    fixedValue = fixedValue.replace(/\s(\d{2})$/g, '.$1');
    needsFix = true;
    confidence = 0.9;
  }

  // Check for comma with less than 3 digits following it (should be period)
  const commaWithLessThanThreeDigits = /,(\d{1,2})(?!\d)/g;
  if (commaWithLessThanThreeDigits.test(fixedValue)) {
    fixedValue = fixedValue.replace(/,(\d{1,2})(?!\d)/g, '.$1');
    needsFix = true;
    confidence = 0.8;
  }

  // Check for period with more than 2 digits following it (should be comma)
  const periodWithMoreThanTwoDigits = /\.(\d{3,})/g;
  if (periodWithMoreThanTwoDigits.test(fixedValue)) {
    fixedValue = fixedValue.replace(/\.(\d{3,})/g, ',$1');
    needsFix = true;
    confidence = 0.8;
  }

  return {
    originalValue: value,
    fixedValue,
    needsFix,
    confidence,
    fieldName,
    fixable: true,
    hasNonFixableContent: hasDigitAndLetter || dollarNotAtStart,
  };
};

/**
 * Applies the OCR fix to a currency value
 */
export const fixOCRCurrencyValue = (value: string): string => {
  const result = analyzeOCRCurrencyValue(value, '');
  return result.fixedValue;
};

/**
 * Fixes OCR issue where "$" is misread as a digit or "S" (e.g., "5 10,395,000.00" or "S 15,900,000.00")
 * Detects patterns like: single digit/S + space + number
 * and corrects them by removing the digit/S and space (keeping just the number)
 */
export const fixMisreadCurrencySymbol = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';

  const str = String(value).trim();
  if (!str) return str;

  // Pattern: single digit (0-9) or "S" followed by one or more spaces, then a number
  // The number can have commas, decimals
  // Examples: "5 10,395,000.00", "S 15,900,000.00", "3 1,234.56", "9 500,000", "2 1234.50", "5 10"
  // This pattern indicates "$" was misread as a digit or "S" - there shouldn't be a space between numbers in a numeric column
  const misreadPattern = /^([\dS])\s+([\d,]+(?:\.\d+)?)$/;
  const match = str.match(misreadPattern);

  if (match) {
    // Return just the number part (the "$" symbol will be handled by parseCurrency if needed)
    return match[2];
  }

  return str;
};

/**
 * Formats a date to mm/dd/yy format
 */
export const formatDateToMMDDYY = (date: Date | string): string => {
  // Use parseISO for strings so date-only values are treated as local midnight.
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (isNaN(d.getTime())) {
    return '';
  }
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const year = d.getFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
};

// Helper to determine if a value is zero or empty (treats various currency formats)
export const isZeroOrEmpty = (value: unknown) => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim();
  if (!str) return true;
  // detect negative in parentheses
  const isNegativeFormat = /\(\s*[$]?\s*[\d,.\s]+\s*\)/.test(str);
  const cleaned = str.replace(/[$,()\s:]/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return false; // has some non-numeric content -> treat as value present
  const signed = isNegativeFormat ? -num : num;
  return signed === 0;
};

// change order regex
export const coRegex = /(?<![a-zA-Z])(co|change\s*orders?|pcco)\b/i;
// Utility function to compare with buffer
export const differsWithBuffer = (a: any, b: any, buffer = 1.0) => {
  if (a === undefined || b === undefined) return false;

  // If both are numeric, apply buffer check (avoid treating empty/null as 0)
  const isNumericValue = (v: unknown) =>
    (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)));

  const numA = isNumericValue(a) ? Number(a) : NaN;
  const numB = isNumericValue(b) ? Number(b) : NaN;
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return Math.abs(numA - numB) >= buffer;
  }

  // Otherwise, compare directly
  return a !== b;
};

export const parseDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Format date to "3rd January 2025" format using date-fns
 */
export const formatDateWithOrdinal = (dateValue: string): string => {
  if (!dateValue) return '';

  try {
    // Handle ISO date strings (with or without time)
    const dateString = dateValue.split('T')[0]; // Extract date part if ISO format
    const date = parseISO(dateString);

    if (!isValid(date)) {
      return dateValue; // Return original if parsing fails
    }

    // 'do' gives ordinal day (1st, 2nd, 3rd, etc.), 'MMMM' gives full month name, 'yyyy' gives 4-digit year
    return formatDateFns(date, 'do MMMM yyyy');
  } catch {
    return dateValue; // Return original if any error occurs
  }
};
